import { useState, type Dispatch, type SetStateAction } from 'react'
import { message } from 'antd'
import { DesktopOutlined } from '@ant-design/icons'
import { HiOutlineExternalLink, HiOutlineMail } from 'react-icons/hi'
import { api } from '@/lib/apiClient'

type Participant = {
  id: string
  name: string
  email: string
  isWinner: boolean
  registrantId?: string
}

type Category = {
  id: string
  name: string
  prizeName: string
  winnerCount: number
  participants: Participant[]
}

type Winner = {
  id: string
  name: string
  email: string
  prize: string
  category: string
  categoryId?: string
}

type Props = {
  categories: Category[]
  eventSlug: string
  onCategoriesChange: (categories: Category[]) => void
  onWinnersChange: Dispatch<SetStateAction<Winner[]>>
}

const DrawOnBigScreen = ({ categories, eventSlug }: Props) => {
  const [activeWinnerId, setActiveWinnerId] = useState<string | null>(null)
  const [updatingWinnerId, setUpdatingWinnerId] = useState<string | null>(null)
  const [isResetting, setIsResetting] = useState(false)

  const hasCategories = categories.length > 0 && categories.some((c) => c.participants.length > 0)

  const updateBigScreenWinner = async (
    winnerName: string | null,
    prize: string | null,
    categoryName: string | null,
    winnerId?: string
  ) => {
    if (winnerId) {
      setUpdatingWinnerId(winnerId)
    } else {
      setIsResetting(true)
    }

    try {
      const data = await api.patch<any>(`/events/${eventSlug}/lucky_draw/big_screen_winner`, {
        winnerName,
        prize,
        category: categoryName,
        reset: winnerName === null,
      })

      if (data.success) {
        if (winnerId) {
          setActiveWinnerId(winnerId)
          message.success(`${winnerName} sent to the big screen.`)
        } else {
          setActiveWinnerId(null)
          message.success('Draw screen reset.')
        }
      } else {
        throw new Error('Unable to update big screen winner.')
      }
    } catch (error) {
      console.error('Error updating big screen winner:', error)
      message.error('Failed to update big screen winner')
    } finally {
      setUpdatingWinnerId(null)
      setIsResetting(false)
    }
  }

  const isBusy = Boolean(updatingWinnerId || isResetting)

  const openBigScreen = () => {
    window.open(`/lucky-draw/${eventSlug}`, '_blank', 'noopener,noreferrer')
  }

  const actionButtons = (
    <div className="ml-auto flex items-center gap-2">
      <button
        type="button"
        onClick={openBigScreen}
        disabled={isBusy}
        className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-sm border border-[#D9D9D9] shadow-[0px_2px_0px_rgba(0,0,0,0.02)] text-sm text-black/85 leading-[22px] hover:border-[#40a9ff] hover:text-[#40a9ff] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <HiOutlineExternalLink className="text-base" />
        Open Big Screen
      </button>
      <button
        type="button"
        onClick={() => updateBigScreenWinner(null, null, null)}
        disabled={isBusy}
        className="px-3 py-1.5 bg-white rounded-sm border border-[#D9D9D9] shadow-[0px_2px_0px_rgba(0,0,0,0.02)] text-sm text-black/85 leading-[22px] hover:border-[#ff7875] hover:text-[#ff7875] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isResetting ? 'Resetting...' : 'Reset Draw Screen'}
      </button>
    </div>
  )

  if (!hasCategories) {
    return (
      <div className="p-6">
        <div className="bg-white border border-[#EBEBEB] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-8 py-5 bg-white border-b border-[#EBEBEB] flex items-center gap-3">
            <DesktopOutlined className="text-xl text-black/85" />
            <span className="text-xl font-normal leading-6 text-black/85">Draw on Big Screen</span>
            {actionButtons}
          </div>

          <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-6 rounded-full bg-blue-50 p-6">
              <HiOutlineMail className="text-5xl text-blue-500" />
            </div>
            <p className="text-black/45 mb-6">
              No data yet. Please configure winners on Settings tab.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="bg-white border border-[#EBEBEB] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-5 bg-white border-b border-[#EBEBEB] flex items-center gap-3">
          <DesktopOutlined className="text-xl text-black/85" />
          <span className="text-xl font-normal leading-6 text-black/85">Draw on Big Screen</span>
          {actionButtons}
        </div>

        {/* Content */}
        <div className="p-8 flex flex-col items-center gap-5">
          {/* Note Section */}
          <div className="w-full flex flex-col gap-2">
            <span className="inline-flex px-2.5 py-0 bg-[#323232] rounded-full text-white text-xs leading-[22px] w-fit">
              Note:
            </span>
            <p className="text-sm text-black/45 leading-6 m-0">
              Next to each winner is a draw button. When clicked, it will send the winner's full
              name to the big screen.
            </p>
          </div>

          {/* Category Sections */}
          {categories.map(
            (category) =>
              category.participants.length > 0 && (
                <div key={category.id} className="w-full p-8 bg-[#DAEDFF] flex flex-col gap-5">
                  {/* Category Title */}
                  <span className="text-sm font-bold text-black/85 leading-[22px]">
                    Category: {category.name}
                  </span>

                  {/* Participants List */}
                  <div className="flex flex-col gap-2">
                    {category.participants.map((participant) => (
                      <div key={participant.id} className="w-full pb-1 flex items-center gap-2.5">
                        <div className="flex-1 flex items-center gap-2.5">
                          <span className="text-sm text-black/85 leading-[22px]">
                            {participant.name}
                          </span>
                          <span className="text-sm text-black/45 leading-[22px]">
                            {participant.email}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            updateBigScreenWinner(
                              participant.name,
                              category.prizeName,
                              category.name,
                              participant.id
                            )
                          }
                          disabled={isBusy}
                          className="px-3 py-1 bg-white rounded-sm border border-[#D9D9D9] shadow-[0px_2px_0px_rgba(0,0,0,0.02)] text-sm text-black/85 leading-[22px] hover:border-[#40a9ff] hover:text-[#40a9ff] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {updatingWinnerId === participant.id
                            ? 'Drawing...'
                            : activeWinnerId === participant.id
                            ? '✓ Drawn'
                            : '✨ Draw Now'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
          )}
        </div>
      </div>
    </div>
  )
}

export default DrawOnBigScreen
